/**
 * @file: 画布区域相关图形算法
 * @author: xiaolong.huang@dmall.com
 */

import Utils from '../utils/util';

interface CFSParams {
    // 容器宽度
    continerWidth: number,

    // 容器高度
    continerHeight: number,

    // 字符串长度
    strLength: number,

    // 字体大小上限
    maxFontSize: number
}

// 根据容器区域大小来计算文字适配大小
export function computeFontSize(params: CFSParams): number {
    let continerArea: number = params.continerWidth * params.continerHeight;
    let fontArea: number = Math.ceil(continerArea / params.strLength);
    let fontHeight: number = Math.floor(Math.sqrt(fontArea));

    return fontHeight > params.maxFontSize ? params.maxFontSize : fontHeight;
}

// 模块空格均匀分布时，间隔宽度计算
export interface SpaceAroundWidthParams {
    spaceTotal: number,
    moduleTotalWidth: number,
    moduleCount: number
}
export function spaceAroundWidth(params: SpaceAroundWidthParams): number {
    let beLeftSpace: number = Utils.math(params.spaceTotal, params.moduleTotalWidth, '-');

    if (beLeftSpace < 0) {
        return 0;
    }

    return Utils.math(beLeftSpace, Utils.math(params.moduleCount, 1, '+'), '/');
}

// 模块贴边均匀分布时，间隔宽度计算
export interface SpaceBetweenWidthParams {
    spaceTotal: number,
    moduleTotalWidth: number,
    moduleCount: number
}
export function spaceBetweenWidth(params: SpaceBetweenWidthParams): number {
    let beLeftSpace: number = Utils.math(params.spaceTotal, params.moduleTotalWidth, '-');

    if (params.moduleCount <= 0) {
        return 0;
    }

    if (beLeftSpace <= 0) {
        return 0;
    }

    return Utils.math(beLeftSpace, Utils.math(params.moduleCount, 1, '-'), '/');
}

// 碰撞检测
export function beTouch(userRect: Rect, targetRect: Rect): boolean {
    // x轴
    var dragLeft = userRect.left;
    var dragRight = dragLeft + userRect.width;
    var targetLeft = targetRect.left;
    var targetRight = targetLeft + targetRect.width;

    // y轴
    var dragTop = userRect.top;
    var dragBottom = dragTop + userRect.height;
    var targetTop = targetRect.top;
    var targetBottom = targetTop + targetRect.height;

    return !(dragRight < targetLeft || dragLeft > targetRight) && !(dragBottom < targetTop || dragTop > targetBottom);
}

// 相交检测
export function beIntersect(userRect: Rect, targetRect: Rect): boolean {
    // x轴
    var dragLeft = userRect.left;
    var dragRight = dragLeft + userRect.width;
    var targetLeft = targetRect.left;
    var targetRight = targetLeft + targetRect.width;

    // y轴
    var dragTop = userRect.top;
    var dragBottom = dragTop + userRect.height;
    var targetTop = targetRect.top;
    var targetBottom = targetTop + targetRect.height;

    return !(dragRight <= targetLeft || dragLeft >= targetRight) && !(dragBottom <= targetTop || dragTop >= targetBottom);
}

// 两矩形相交面积计算
export function mixedArea(userRect: Rect, targetRect: Rect): number {
    if (!beIntersect(userRect, targetRect)) {
        return 0;
    }

    var xBeClose = false;
    var yBeClose = false;
    var disX = 0;
    var disY = 0;

    // x轴
    var dragLeft = userRect.left;
    var dragRight = dragLeft + userRect.width;
    var targetLeft = targetRect.left;
    var targetRight = targetLeft + targetRect.width;

    // y轴
    var dragTop = userRect.top;
    var dragBottom = dragTop + userRect.height;
    var targetTop = targetRect.top;
    var targetBottom = targetTop + targetRect.height;

    // x轴检测
    xBeClose = dragLeft <= targetRight && dragRight >= targetLeft;
    // y轴检测
    yBeClose = dragBottom >= targetTop && dragTop <= targetBottom;

    if (dragLeft < targetRight && dragLeft > targetLeft && dragRight > targetRight) {
        disX = targetRight - dragLeft;
    } else if (dragRight > targetLeft && dragRight < targetRight && dragLeft < targetLeft) {
        disX = dragRight - targetLeft;
    } else if(dragLeft >= targetLeft && dragRight <= targetRight) {
        disX = userRect.width;
    } else {
        disX = targetRect.width;
    }

    if (dragTop < targetBottom && dragTop > targetTop && dragBottom > targetBottom) {
        disY = targetBottom - dragTop;
    } else if (dragBottom > targetTop && dragBottom < targetBottom && dragTop < targetTop) {
        disY = dragBottom - targetTop;
    } else if (dragTop >= targetTop && dragBottom <= targetBottom) {
        disY = userRect.height;
    } else {
        disY = targetRect.height;
    }

    return disX * disY;
}

// beContainRect 是否被 containRect 包含一半的面积
export function beContainHalf(beContainRect: Rect, containRect: Rect): boolean {
    let mixedAreaValue: number = mixedArea(beContainRect, containRect);
    let beContainRectArea: number = Utils.math(beContainRect.width, beContainRect.height, '*');

    return mixedAreaValue >= Utils.math(beContainRectArea, 2, '/');
}

// beContainRect 是否在 containRect 内部
export function isInsideRect(beContainRect: Rect, containRect: Rect): boolean {
    // x轴
    var dragLeft = beContainRect.left;
    var dragRight = dragLeft + beContainRect.width;
    var targetLeft = containRect.left;
    var targetRight = targetLeft + containRect.width;

    // y轴
    var dragTop = beContainRect.top;
    var dragBottom = dragTop + beContainRect.height;
    var targetTop = containRect.top;
    var targetBottom = targetTop + containRect.height;

    return dragLeft >= targetLeft && dragRight <= targetRight
        && dragTop >= targetTop && dragBottom <= targetBottom;
}

// 生成一组矩形的边界矩形框
export function createRectsBorder(rects: Array<Rect>): Rect {
    if (!rects.length) {
        return {
            width: 0,
            height: 0,
            left: 0,
            top: 0
        };
    }
    let left: number = Number.MAX_VALUE;
    let right: number = Number.MIN_VALUE;
    let top: number = Number.MAX_VALUE;
    let bottom: number = Number.MIN_VALUE;

    rects.forEach((rect: Rect): void => {
        left = Math.min(left, rect.left);
        top = Math.min(top, rect.top);
        right = Math.max(right, Utils.math(rect.left, rect.width, '+'));
        bottom = Math.max(bottom, Utils.math(rect.top, rect.height, '+'));
    });

    return {
        left,
        top,
        width: Utils.math(right, left, '-'),
        height: Utils.math(bottom, top, '-')
    };
}

export function pointInRect(point: MousePoint, rect: Rect): boolean {
    let xBeClose: boolean = !(point.left < rect.left || point.left > Utils.math(rect.left, rect.width, '+'));
    let yBeClose: boolean = !(point.top < rect.top || point.top > Utils.math(rect.top, rect.height, '+'));

    return xBeClose && yBeClose;
}

export function pointInRectAboveOfBottom(point: MousePoint, rect: Rect): boolean {
    let aboveOfBottom: boolean = point.top <= Utils.math(rect.top, rect.height, '+');

    return pointXInRect(point, rect) && aboveOfBottom;
}

// 点x轴位于矩形内
export function pointXInRect(point: MousePoint, rect: Rect): boolean {
    let xBeClose: boolean = !(point.left < rect.left || point.left > Utils.math(rect.left, rect.width, '+'));

    return xBeClose;
}

// 点在矩形内的方位 left|right
export function pointInRectdirection(point: MousePoint, rect: Rect): PointInRectdirection {
    let halfWidth: number = Utils.math(rect.width, 2, '/');
    let xMiddle: number = Utils.math(rect.left, halfWidth, '+');

    if (point.left <= xMiddle) {
        return 'left';
    }

    return 'right';
}

// 点在矩形内X轴方位 left|right
export function pointInRectdirectionX(point: MousePoint, rect: Rect): PointInRectdirection {
    return pointInRectdirection(point, rect);
}

// 点在矩形内Y轴方位 top|bottom
export function pointInRectdirectionY(point: MousePoint, rect: Rect): PointInRectdirection {
    let halfHeight: number = Utils.math(rect.height, 2, '/');
    let yMiddle: number = Utils.math(rect.top, halfHeight, '+');

    if (point.top <= yMiddle) {
        return 'top';
    }

    return 'bottom';
}

export function pointOfRectDirection(point: MousePoint, rect: Rect, middleArea: number): PointOfRectDirection {
    let middleAreaWidth: number = Utils.math(rect.width, middleArea, '*');

    return pointInRectDirection(point, rect, middleAreaWidth);
}

export function pointInRectDirection(point: MousePoint, rect: Rect, middleAreaWidth: number): PointOfRectDirection {
    let slideWidth: number = Utils.math(rect.width, middleAreaWidth, '-');
    let singleSlideWidth: number = Utils.math(slideWidth, 2, '/');
    let leftLine: number = Utils.math(rect.left, singleSlideWidth, '+');
    let rightLine: number = Utils.math(leftLine, middleAreaWidth, '+');

    if (point.left <= leftLine) {
        return 'left';
    }

    if (point.left > leftLine && point.left < rightLine) {
        return 'middle';
    }

    return 'right';
}

interface RectMapByTopKey {
    top?: Array<Rect>
};
export function theBottomRect(rects: Array<Rect>): Rect|null {
    if (!rects.length) {
        return null;
    }
    let rectsAddBottom: Array<FreeObjMap> = rects.map((rect: Rect): FreeObjMap => {
        return {
            ...rect,
            bottom: Utils.math(rect.top, rect.height, '+')
        };
    });
    let sortByBottom: Array<FreeObjMap> = Utils.quickSortByKey(rectsAddBottom, 'bottom').reverse();
    let rectMap: RectMapByTopKey = {};

    // 按top分组
    sortByBottom.forEach((rect: FreeObjMap) => {
        let bottomStr: string = rect.bottom + '';

        if (!(bottomStr in rectMap)) {
            rectMap[bottomStr] = [];
        }

        rectMap[bottomStr].push(rect);
    });
    let maxBottom: string = sortByBottom[0].bottom + '';
    let sortByLeft: Array<FreeObjMap> = Utils.quickSortByKey(rectMap[maxBottom], 'left').reverse();

    return {
        left: sortByLeft[0].left,
        top: sortByLeft[0].top,
        width: sortByLeft[0].width,
        height: sortByLeft[0].height
    };
}

// 相对坐标计算
export function relativeRect(targetRect: Rect, baseRect: Rect): Rect {
    return {
        left: Utils.math(targetRect.left, baseRect.left, '-'),
        top: Utils.math(targetRect.top, baseRect.top, '-'),
        width: targetRect.width,
        height: targetRect.height
    };
}

// 矩形内部X轴吸附
export function touchRectInsideX(touch: Rect, target: Rect, sensitive: LogicsicsMeasurement): boolean {
    let touchRight: number = Utils.math(touch.left, touch.width, '+');
    let beTouchRight: number = Utils.math(target.left, target.width, '+');
    let leftTouch: number = Utils.math(touch.left, target.left, '-');
    let rightTouch: number = Utils.math(beTouchRight, touchRight, '-');
    let insideXTouch: boolean = (leftTouch >= 0 && leftTouch <= sensitive) || (rightTouch >= 0 && rightTouch <= sensitive);

    return beTouch(touch, target) && insideXTouch;
}

export function stuckToRectInside(touch: Rect, targets: Rect[], sensitive: LogicsicsMeasurement): Rect|null {
    for (let i = 0; i < targets.length; i++) {
        let stuck: StuckToRectInsideX = stuckToRectInsideX(touch, targets[i], sensitive);

        if (!stuck.isStuck) {
            continue;
        }

        if (stuck.dir === 'left') {
            return {
                left: targets[i].left,
                top: touch.top,
                width: touch.width,
                height: touch.height
            };
        }

        let rightLine: number = Utils.math(targets[i].left, targets[i].width, '+');

        return {
            left: Utils.math(rightLine, touch.width, '-'),
            top: touch.top,
            width: touch.width,
            height: touch.height
        };
    }

    return {
        ...touch
    };
}

interface StuckToRectInsideX {
    isStuck: boolean,
    dir: string
}
function stuckToRectInsideX(touch: Rect, target: Rect, sensitive: LogicsicsMeasurement): StuckToRectInsideX {
    let touchRight: number = Utils.math(touch.left, touch.width, '+');
    let beTouchRight: number = Utils.math(target.left, target.width, '+');
    let leftTouch: number = Utils.math(touch.left, target.left, '-');
    let rightTouch: number = Utils.math(beTouchRight, touchRight, '-');
    let left: boolean = leftTouch >= 0 && leftTouch <= sensitive;
    let right: boolean = rightTouch >= 0 && rightTouch <= sensitive;
    let insideXTouch: boolean = left || right;

    return {
        isStuck: beTouch(touch, target) && insideXTouch,
        dir: left ? 'left' : 'right'
    };
}

// 两矩形是否碰撞且顶部对齐
export function rectTopAlign(target: Rect, base: Rect): boolean {
    let targetTop: number = target.top;
    let baseTop: number = base.top;

    return targetTop === baseTop && beTouch(target, base);
}

export function stepValue(stepLen: number, value: number): number {
    if (value <= 0) {
        return 0;
    }

    let temp: number = Utils.math(value, stepLen, '/');

    return Utils.math(Math.round(temp), stepLen, '*');
}
